import React, { useMemo } from "react";

/** The picker's own contact shape. Both sources normalise into this: the live
 *  GHL listing (GhlContact) and the model's list_outreach_recipients payload
 *  (OutreachContact), which name their fields differently. */
export interface PickerContact {
  id: string;
  name: string;
  email: string;
  company?: string;
  tags?: string[];
}

interface RecipientPickerCardProps {
  contacts: PickerContact[];
  /** Total matching the current search across the whole location, not just what is loaded. */
  total: number;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  /** Select or clear every contact currently visible after search + tag filtering. */
  onToggleVisible: (ids: string[], select: boolean) => void;
  /** What is typed in the box, which is not necessarily what has been searched for. */
  search: string;
  onSearchChange: (next: string) => void;
  /** Runs the search against every contact in the location. */
  onSearchSubmit: () => void;
  /** The term the loaded rows actually came back for — "" means the full list. */
  appliedQuery: string;
  activeTag: string | null;
  onTagChange: (tag: string | null) => void;
  isSearching?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onSend: (contactIds: string[]) => void;
  /** Set while the campaign request is in flight — the whole card goes inert. */
  isSending?: boolean;
  error?: string | null;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

const avatarTints: React.CSSProperties[] = [
  { background: "color-mix(in srgb, var(--color-primary) 16%, transparent)", color: "var(--color-primary)" },
  { background: "color-mix(in srgb, var(--color-secondary) 18%, transparent)", color: "var(--color-primary)" },
  {
    background: "color-mix(in srgb, var(--color-accent-warm) 24%, transparent)",
    color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
  },
];

export const RecipientPickerCard: React.FC<RecipientPickerCardProps> = ({
  contacts,
  total,
  selectedIds,
  onToggle,
  onToggleVisible,
  search,
  onSearchChange,
  onSearchSubmit,
  appliedQuery,
  activeTag,
  onTagChange,
  isSearching = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onSend,
  isSending = false,
  error = null,
}) => {
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    contacts.forEach((c) => c.tags?.forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  }, [contacts]);

  // Search runs on the server against every contact in the location. Tags are not
  // searchable there, so tag narrowing is applied to what has been loaded.
  const visible = useMemo(
    () => (activeTag ? contacts.filter((c) => c.tags?.includes(activeTag)) : contacts),
    [contacts, activeTag],
  );

  const visibleIds = visible.map((c) => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const sendableIds = Array.from(selectedIds);

  // Typing alone changes nothing — the rows on screen still belong to the last
  // search that ran, so say so rather than letting them look like results.
  const unsubmitted = search.trim() !== appliedQuery.trim();

  const headerIconStyle: React.CSSProperties = {
    background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)",
  };

  return (
    <div className="my-3 w-full min-w-0 max-w-full rounded-2xl border border-theme-border bg-theme-surface-ui p-4">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3">
        <span
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl"
          style={headerIconStyle}
        >
          <span className="material-icons text-[18px] text-theme-primary">group</span>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-theme-fg leading-tight">Choose recipients</p>
          <p className="text-xs text-theme-fg-muted">
            {contacts.length} of {total} contact{total !== 1 ? "s" : ""} loaded
            {appliedQuery.trim() ? ` matching "${appliedQuery.trim()}"` : ""}
          </p>
        </div>
      </div>

      {/* Search — runs against every contact in the GHL location, not the loaded rows */}
      <form
        className="mb-1 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          onSearchSubmit();
        }}
      >
        <div className="relative min-w-0 flex-1">
          <span className="material-icons absolute left-2.5 top-1/2 -translate-y-1/2 text-[16px] text-theme-fg-muted pointer-events-none">
            search
          </span>
          <input
            type="text"
            className="w-full rounded-lg border border-theme-border bg-theme-surface pl-8 pr-8 py-1.5 text-sm text-theme-fg placeholder:text-theme-fg-muted focus:outline-none focus:ring-1 focus:ring-theme-primary"
            placeholder="Search all contacts by name, email, or phone..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={isSending}
          />
          {isSearching ? (
            <span className="material-icons absolute right-2.5 top-1/2 -translate-y-1/2 text-[16px] text-theme-fg-muted animate-spin">
              progress_activity
            </span>
          ) : (
            search.length > 0 && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  onSearchChange("");
                  onSearchSubmit();
                }}
                disabled={isSending}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-theme-fg-muted hover:text-theme-fg disabled:opacity-60"
              >
                <span className="material-icons text-[16px]">close</span>
              </button>
            )
          )}
        </div>
        <button
          type="submit"
          disabled={isSearching || isSending}
          className="shrink-0 rounded-lg bg-theme-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Search
        </button>
      </form>

      <p className="mb-2 text-[11px] text-theme-fg-muted">
        {unsubmitted
          ? "Press Search to look through every contact, not just the ones loaded."
          : "Searches your whole GoHighLevel contact list, not only the loaded rows."}
      </p>

      {/* Tag filter — narrows the loaded rows; GHL cannot search on tags */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <span className="material-icons text-[14px] text-theme-fg-muted">label</span>
          {allTags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => onTagChange(activeTag === tag ? null : tag)}
              disabled={isSending}
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                activeTag === tag
                  ? "bg-theme-primary text-white"
                  : "bg-theme-secondary/15 text-theme-fg-muted hover:bg-theme-secondary/25"
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {tag}
            </button>
          ))}
          {activeTag && (
            <button
              type="button"
              onClick={() => onTagChange(null)}
              disabled={isSending}
              className="text-[11px] text-theme-fg-muted hover:text-theme-fg underline disabled:opacity-60"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Select all visible */}
      {visible.length > 0 && (
        <div className="flex items-center gap-2 py-1.5 px-1 mb-1">
          <input
            type="checkbox"
            className="h-4 w-4 rounded accent-[var(--color-primary)]"
            checked={allVisibleSelected}
            onChange={() => onToggleVisible(visibleIds, !allVisibleSelected)}
            disabled={isSending}
            id="select-all-recipients"
          />
          <label
            htmlFor="select-all-recipients"
            className="text-xs text-theme-fg-muted select-none cursor-pointer"
          >
            {allVisibleSelected ? "Deselect these" : `Select these ${visibleIds.length}`}
          </label>
          {selectedIds.size > 0 && (
            <span
              className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 14%, transparent)",
                color: "var(--color-primary)",
              }}
            >
              {selectedIds.size} selected
            </span>
          )}
        </div>
      )}

      {/* Contact list */}
      <div className="max-h-72 overflow-y-auto flex flex-col gap-1 mb-3">
        {visible.length === 0 ? (
          <p className="text-sm text-theme-fg-muted text-center py-4">
            {isSearching ? "Searching..." : "No contacts match."}
          </p>
        ) : (
          visible.map((contact, i) => {
            const tint = avatarTints[i % avatarTints.length];
            const isSelected = selectedIds.has(contact.id);
            const noEmail = !contact.email;
            return (
              <label
                key={contact.id}
                className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 transition-colors select-none ${
                  isSelected ? "border-theme-primary bg-theme-surface" : "border-transparent bg-theme-surface"
                } ${noEmail || isSending ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-theme-border"}`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 rounded accent-[var(--color-primary)]"
                  checked={isSelected}
                  onChange={() => onToggle(contact.id)}
                  disabled={noEmail || isSending}
                />
                <div
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                  style={tint}
                >
                  {initials(contact.name || contact.email || "?")}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-theme-fg leading-tight">
                    {contact.name || contact.email || contact.id}
                  </p>
                  <p className="truncate text-xs text-theme-fg-muted">
                    {contact.email || "No email — cannot be emailed"}
                  </p>
                </div>
              </label>
            );
          })
        )}
      </div>

      {hasMore && !activeTag && (
        <button
          type="button"
          onClick={() => onLoadMore?.()}
          disabled={isLoadingMore || isSending}
          className="mb-3 w-full rounded-xl border border-theme-border py-1.5 text-xs font-medium text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 disabled:opacity-60"
        >
          {isLoadingMore ? "Loading..." : "Load more contacts"}
        </button>
      )}

      {error && <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

      <button
        type="button"
        onClick={() => onSend(sendableIds)}
        disabled={sendableIds.length === 0 || isSending}
        className="w-full rounded-xl bg-theme-primary py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isSending
          ? "Starting campaign..."
          : sendableIds.length === 0
            ? "Send"
            : `Send to ${sendableIds.length} ${sendableIds.length === 1 ? "recipient" : "recipients"}`}
      </button>
    </div>
  );
};
